<script setup>
import { Link, usePage } from '@inertiajs/vue3';
import { ref, computed, onMounted, onUnmounted, provide, watch, nextTick, defineAsyncComponent } from 'vue';
import { Bars3Icon, XMarkIcon, ArrowUpIcon } from '@heroicons/vue/24/outline';
import { getDesignVariant } from '@/lib/designVariants';
import { getDesignSet, designSetToCssVars } from '@/lib/designSets';
import { getThemeForDomain, themeToCssVars } from '@/lib/themes';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import { getVariantConfig, variantThemeToCssVars, VARIANT_CHROME } from '@/Variants/registry';
import { useFocusTrap } from '@/composables/useFocusTrap';
const VariantChrome = defineAsyncComponent(() => import('@/Components/VariantChrome.vue'));
// Vue 3 script-setup auto-resolves `vXxx`-named imports as local
// directives, sidestepping the app-context lookup that was failing
// during SSR ("Cannot read properties of undefined (reading
// 'getSSRProps')"). Local resolution is independent of app.directive()
// registration so this works in both client + SSR.
import { vGlossary } from '@/directives/glossaryHighlight';

const props = defineProps({
    domain: { type: Object, required: true },
    branding: { type: Object, default: null },
    activePage: { type: String, default: '' },
    heroMode: { type: Boolean, default: false },
    accentColor: { type: String, default: '' },
    designVariant: { type: Object, default: null },
    service: { type: String, default: 'pv' },
    // When true, AppLayout suppresses its own header/footer so a child
    // (e.g. VariantPageWrapper mounting per-domain variant chrome) can
    // own the chrome instead. Independent of the `?variant=` query path.
    hideChrome: { type: Boolean, default: false },
});

const page = usePage();

// Design Set: fixed to classic (1) - no random visual variation
const activeDesignSetId = 1;
const activeDesignSet = getDesignSet(activeDesignSetId);
const designSetCssVars = designSetToCssVars(activeDesignSet);
provide('designSetId', activeDesignSetId);

const scrolled = ref(false);
const showBackToTop = ref(false);
const mobileMenuOpen = ref(false);

const hexToRgb = (hex) => {
    const normalized = String(hex || '').replace('#', '');

    if (!/^[0-9a-f]{6}$/i.test(normalized)) {
        return null;
    }

    return {
        r: Number.parseInt(normalized.slice(0, 2), 16),
        g: Number.parseInt(normalized.slice(2, 4), 16),
        b: Number.parseInt(normalized.slice(4, 6), 16),
    };
};

const withAlpha = (hex, alpha) => {
    const rgb = hexToRgb(hex);

    return rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})` : hex;
};

const contrastTextColor = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return '#ffffff';
    const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return lum > 0.55 ? '#1a1a1a' : '#ffffff';
};

const handleScroll = () => {
    scrolled.value = window.scrollY > 20;
    showBackToTop.value = window.scrollY > 400;
};

onMounted(() => window.addEventListener('scroll', handleScroll, { passive: true }));
onUnmounted(() => window.removeEventListener('scroll', handleScroll));

const domainName = computed(() => props.domain?.request_host || props.domain?.name || 'leadportal.scienceworks.com');
const contactEmail = computed(() => props.domain?.settings?.contact_email || (domainName.value.includes('scienceworks.com') ? 'info@scienceworks.com' : `info@${domainName.value}`));

const pickFilled = (domainValue, globalValue, fallback) => {
    if (typeof domainValue === 'string') {
        if (domainValue.trim() !== '') {
            return domainValue;
        }
    } else if (domainValue !== null && domainValue !== undefined) {
        return domainValue;
    }

    if (typeof globalValue === 'string') {
        if (globalValue.trim() !== '') {
            return globalValue;
        }
    } else if (globalValue !== null && globalValue !== undefined) {
        return globalValue;
    }

    return fallback;
};

const activeVariant = computed(() => {
    if (props.designVariant?.id) {
        return getDesignVariant(props.designVariant.id, props.service || 'pv');
    }
    return null;
});

// Variant slug propagated by Inertia shared props (HandleInertiaRequests).
// When set, the matching VARIANT_REGISTRY entry overrides AppLayout's
// theme tokens, brand text, nav links and CTA so interior pages keep
// the look of the active design variant. Independent of (and applied
// after) the existing `props.designVariant` path.
const activeVariantSlug = computed(() => {
    const slug = page.props?.activeVariant;
    return typeof slug === 'string' && slug.length > 0 ? slug : null;
});

const activeVariantConfig = computed(() => getVariantConfig(activeVariantSlug.value));

// WAEMO is a focused direct-installer brand: suppress the portal-style footer
// columns (Produkte/Services + Tools & Wissen = Rechner, Glossar, Ratgeber,
// Forum) that don't belong on its site.
const isWaemoBrand = computed(() => {
    const host = (props.domain?.request_host || props.domain?.name || '').toLowerCase().replace(/^www\./, '');
    return host === 'waemo.de' || activeVariantSlug.value === 'waemo';
});

const themeStyles = computed(() => {
    const v = activeVariant.value;
    let styles = {};

    if (v) {
        styles = {
            '--brand-accent': v.palette.accent,
            '--brand-accent-soft': v.palette.accentSoft,
            '--dv-page': v.palette.page,
            '--dv-section': v.palette.section,
            '--dv-section-alt': v.palette.sectionAlt,
            '--dv-card': v.palette.card,
            '--dv-card-alt': v.palette.cardAlt,
            '--dv-text': v.palette.text,
            '--dv-muted': v.palette.muted,
            '--dv-border': v.palette.border,
            '--dv-dark': v.palette.dark,
            '--dv-dark-soft': v.palette.darkSoft,
            '--dv-glow-a': v.palette.glowA,
            '--dv-glow-b': v.palette.glowB,
            '--dv-radius-xl': v.radius.xl,
            '--dv-radius-lg': v.radius.lg,
            '--dv-radius-md': v.radius.md,
            '--dv-shadow-card': v.shadow.card,
            '--dv-shadow-panel': v.shadow.panel,
            '--dv-font-heading': v.typography.heading,
            '--dv-font-body': v.typography.body,
            background: v.palette.page,
            color: v.palette.text,
            ...designSetCssVars,
        };
    } else {
        styles = { ...designSetCssVars };
    }

    // Merge theme CSS vars (--theme-font-heading, --theme-radius-*, etc.)
    Object.assign(styles, domainThemeVars.value);

    // Apply theme font so all pages on branded domains use the correct font
    if (domainTheme.value?.fonts?.body) {
        styles.fontFamily = domainTheme.value.fonts.body;
    }

    // Domain accent_color ALWAYS overrides design variant accent.
    const domainAccent = props.domain?.accent_color;
    if (domainAccent) {
        styles['--brand-accent'] = domainAccent;
        styles['--brand-accent-soft'] = withAlpha(domainAccent, 0.14);
        styles['--brand-accent-text'] = contrastTextColor(domainAccent);
    } else if (props.accentColor) {
        styles['--brand-accent'] = props.accentColor;
        styles['--brand-accent-soft'] = withAlpha(props.accentColor, 0.14);
        styles['--brand-accent-text'] = contrastTextColor(props.accentColor);
    }

    // Fallback
    if (!styles['--brand-accent-text'] && styles['--brand-accent']) {
        styles['--brand-accent-text'] = contrastTextColor(styles['--brand-accent']);
    }

    // enter.de design pattern: accent color is ONLY for backgrounds/buttons/icons.
    // Text that references --brand-accent should display as black instead.
    // --brand-accent-fg provides a text-safe version (always dark/readable).
    const accentForFg = styles['--brand-accent'];
    if (accentForFg) {
        const rgb = hexToRgb(accentForFg);
        if (rgb) {
            const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
            // Bright accents: text should be black. Dark accents: keep as-is.
            styles['--brand-accent-fg'] = lum > 0.45 ? '#1a1a1a' : accentForFg;
        }
    }

    // Domain palette overrides -- lets each domain customize page/card/section colors.
    // Also overrides legacy --bg-cream / --bg-surface / --border-warm / --ink vars
    // used in inline styles and app.css so everything stays consistent.
    const pal = props.domain?.settings?.palette;
    if (pal) {
        if (pal.page)       { styles['--dv-page'] = pal.page; styles['--bg-cream'] = pal.page; styles['--dv-section-alt'] = pal.sectionAlt || pal.page; styles.background = pal.page; }
        if (pal.section)    { styles['--dv-section'] = pal.section; }
        if (pal.sectionAlt) { styles['--dv-section-alt'] = pal.sectionAlt; }
        if (pal.card)       { styles['--dv-card'] = pal.card; styles['--bg-surface'] = pal.card; }
        if (pal.cardAlt)    { styles['--dv-card-alt'] = pal.cardAlt; }
        if (pal.border)     { styles['--dv-border'] = pal.border; styles['--border-warm'] = pal.border; styles['--border-soft'] = pal.border; }
        if (pal.text)       { styles['--dv-text'] = pal.text; styles['--ink'] = pal.text; styles.color = pal.text; }
        if (pal.muted)      { styles['--dv-muted'] = pal.muted; styles['--ink-mute'] = pal.muted; }
    }

    // Active variant (?variant=v05_technical) overrides everything else.
    // Applied last so interior-page chrome inherits the variant's accent,
    // page bg, fg, border and font even when the domain has its own
    // palette settings — the user explicitly opted into the variant.
    if (activeVariantConfig.value?.theme) {
        Object.assign(styles, variantThemeToCssVars(activeVariantConfig.value.theme));
    }

    return styles;
});

// Brand label override from registry (falls back to existing brandName/parts).
const variantBrand = computed(() => activeVariantConfig.value?.brand || null);

// Nav link override. Registry entries with `nav: null` keep the default
// AppLayout nav (used by the Vue-side variants that don't need a swap).
const effectiveNavLinks = computed(() => {
    const cfg = activeVariantConfig.value;
    if (!cfg || !Array.isArray(cfg.nav) || cfg.nav.length === 0) return navLinks;
    return cfg.nav.map((l, i) => ({ href: l.href, label: l.label, key: `variant-${i}` }));
});

// CTA override.
const variantCta = computed(() => activeVariantConfig.value?.cta || null);

// True when the active slug has dedicated React Nav/Footer chrome
// extracted from its variant file. Vue-side variants (waemo/profi/...)
// don't, so AppLayout's standard chrome stays in place — only theme
// tokens override.
const useVariantChrome = computed(() => {
    const slug = activeVariantSlug.value;
    return !!(slug && VARIANT_CHROME[slug]);
});

// Suppresses AppLayout's default header/footer (and the variant-chrome
// path) when a parent has taken responsibility for chrome — e.g. a page
// wraps content in <VariantPageWrapper> that mounts the active domain's
// per-variant Nav + Footer.
// Bespoke pilots own their entire chrome via MissionChrome/<Host>Chrome.vue
// (mounted by VariantPageWrapper). Suppress AppLayout's default header
// and footer on these hosts so we don't render double chrome on legal
// pages (Impressum/Datenschutz/AGB/Widerruf).
const BESPOKE_MISSION_HOSTS = new Set([
    'www-solar.de', 'waermepumpenampel.de', 'hausautarkie.com',
    'heizen-mit-waermepumpe.de', 'heizung-solar-bremen.de',
    'heizungsbaubremen.de', 'heizungssuche.de', 'heizungzukunft.com',
    'my-solar24.de', 'pvcheckpro.com', 'pvheizung.de',
    'pvkostenrechner.com', 'solar-heiztherm.de', 'solar-millennium.de',
    'solar-planen.de', 'solarcheck26.de', 'solar-angebot-vergleich.de',
    'waermepumpensofort.de', 'waermepumpevergleich.de', 'waermepreis.com',
]);
const useBespokeMissionChrome = computed(() => {
    const raw = props.domain?.request_host || props.domain?.name || '';
    return BESPOKE_MISSION_HOSTS.has(raw.toLowerCase().replace(/^www\./, ''));
});
const suppressOwnChrome = computed(() => props.hideChrome || useVariantChrome.value || useBespokeMissionChrome.value);

const brandName = computed(() => props.domain?.settings?.brand_name || null);
const brandCta = computed(() => props.domain?.settings?.brand_cta || null);
const isBrandDirect = computed(() => checkBrandDirect(props.domain));
const logoUrl = computed(() => {
    const logo = props.domain?.settings?.logo;
    if (!logo) return null;
    // Logos starting with / or http are absolute paths (e.g. /images/studivolt-logo.png)
    if (logo.startsWith('/') || logo.startsWith('http')) return logo;
    return '/media/' + logo.replace(/^\//, '');
});
// PSI flagged the brand logo (39 KB PNG natural 571x437) being served
// at its source size when displayed at just 75x58 in the navbar. Route
// PNG/JPG/WebP logos through the SeoController ?w=200 resize endpoint
// (covers 2x retina at the displayed 75-100px width). Absolute or
// external URLs are passed through unchanged.
const logoSrc = computed(() => {
    const u = logoUrl.value;
    if (!u) return null;
    if (u.startsWith('http') || !u.startsWith('/media/')) return u;
    if (!/\.(png|jpe?g|webp)$/i.test(u)) return u;
    const sep = u.includes('?') ? '&' : '?';
    return u + sep + 'w=200';
});

const domainTheme = computed(() => getThemeForDomain(props.domain));
const domainThemeVars = computed(() => themeToCssVars(domainTheme.value));

// Mirror theme CSS vars onto <html> so teleported overlays (fullscreen
// lead funnel, etc.) inherit brand colors even though they live outside
// this layout's subtree. Must be AFTER all deps of themeStyles (activeVariant,
// domainTheme, domainThemeVars) to avoid TDZ during setup.
const applyThemeToRoot = (styles) => {
    if (typeof document === 'undefined' || !styles) return;
    const root = document.documentElement;
    for (const [k, v] of Object.entries(styles)) {
        if (k.startsWith('--') && v != null) root.style.setProperty(k, String(v));
    }
};
onMounted(() => applyThemeToRoot(themeStyles.value));
watch(themeStyles, applyThemeToRoot, { deep: true });

const brandParts = computed(() => {
    const cleanHost = domainName.value.replace(/^www\./, '');
    const parts = cleanHost.split('.');
    const tld = parts.pop() || 'com';
    const base = parts.join('.') || 'leadportal';

    return {
        base,
        tld,
        full: cleanHost,
    };
});

const socialLinks = computed(() => {
    const settings = props.domain?.settings || {};
    return [
        ...(settings.twitter_url ? [{ name: 'X', url: settings.twitter_url, icon: 'x' }] : []),
        ...(settings.facebook_url ? [{ name: 'Facebook', url: settings.facebook_url, icon: 'facebook' }] : []),
        ...(settings.instagram_url ? [{ name: 'Instagram', url: settings.instagram_url, icon: 'instagram' }] : []),
        ...(settings.linkedin_url ? [{ name: 'LinkedIn', url: settings.linkedin_url, icon: 'linkedin' }] : []),
        ...(settings.youtube_url ? [{ name: 'YouTube', url: settings.youtube_url, icon: 'youtube' }] : []),
    ];
});

const navLinks = [
    { href: '/so-funktioniert-es', label: 'So funktioniert es', key: 'how' },
    { href: '/vorteile', label: 'Vorteile', key: 'benefits' },
    { href: '/angebot-vergleichen', label: 'Angebot vergleichen', key: 'offer-compare' },
    { href: '/über-uns', label: 'Über uns', key: 'about' },
    { href: '/kontakt', label: 'Kontakt', key: 'contact' },
];

const trustBarItems = computed(() => isWaemoBrand.value ? [
    '100 % kostenlos & unverbindlich',
    'Beratung, Planung & Installation aus einer Hand',
    'Festpreisgarantie - keine versteckten Kosten',
    'DSGVO-konform & SSL-verschlüsselt',
    'Persönlicher Ansprechpartner statt Call-Center',
] : [
    '100 % kostenlos & unverbindlich',
    'Geprüfte Fachbetriebe aus Ihrer Region',
    'Herstellerunabhängig & transparent',
    'DSGVO-konform & SSL-verschlüsselt',
    'Erste Angebote in 24 h',
]);

const toolLinks = computed(() => {
    const all = [
        { href: '/solarrechner', label: 'Solarrechner' },
        { href: '/wärmepumpen-rechner', label: 'Wärmepumpen-Rechner' },
        { href: '/förderung-finder', label: 'Förderungs-Finder' },
        { href: '/glossar', label: 'Glossar' },
        { href: '/marktanalyse', label: 'Marktanalyse' },
        { href: '/ratgeber', label: 'Ratgeber' },
        { href: '/forum', label: 'Forum' },
    ];
    if (isBrandDirect.value) return all.filter(l => l.href !== '/marktanalyse');
    return all;
});

const pageLinks = [
    { href: '/so-funktioniert-es', label: 'So funktioniert es' },
    { href: '/vorteile', label: 'Vorteile' },
    { href: '/angebot-vergleichen', label: 'Angebot vergleichen' },
    { href: '/über-uns', label: 'Über uns' },
    { href: '/karriere', label: 'Karriere' },
    { href: '/kontakt', label: 'Kontakt' },
];

const serviceLinks = computed(() => {
    const niche = props.domain?.niche;
    const pvLinks = [
        { href: '/photovoltaik', label: 'Photovoltaik' },
        { href: '/photovoltaik-angebot', label: 'Photovoltaik Angebot' },
    ];
    const wpLinks = [
        { href: '/wärmepumpe', label: 'Wärmepumpe' },
        { href: '/wärmepumpe-angebot', label: 'Wärmepumpe Angebot' },
    ];
    if (niche === 'kombi') return [...pvLinks, ...wpLinks];
    if (niche === 'wp' || props.service === 'wp') return wpLinks;
    return pvLinks;
});

const navigateTo = (path) => {
    window.location.assign(new URL(path, window.location.origin).toString());
};

const scrollToFormOrNavigate = () => {
    // Waemo home / any page that registered a direct opener: open fullscreen funnel instantly
    if (window.__waemoOpenFunnel) {
        window.__waemoOpenFunnel();
        return;
    }
    // Broadcast for any page that listens (non-waemo variants can opt in)
    window.dispatchEvent(new CustomEvent('waemo:open-funnel'));

    // Fall back to scrolling to an inline lead form on the current page
    const el = document.getElementById('lead-form-section') || document.getElementById('lead-form-container');
    if (el) {
        const headerOffset = 80;
        const elementPosition = el.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - headerOffset, behavior: 'smooth' });
        return;
    }

    // No form present on current page: jump home with auto-open
    window.location.href = '/?fs=1';
};

const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Fallback: ensure we actually reach the top
    requestAnimationFrame(() => {
        if (window.scrollY > 0) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    });
};

const openCookiePreferences = () => {
    if (window.CookieConsent?.showPreferences) {
        window.CookieConsent.showPreferences();
    }
};

// Focus trap for mobile menu — uses shared useFocusTrap composable so
// the same Tab cycling, Esc-handling, and return-focus behavior applies
// consistently across modals/menus across the portal.
const mobileMenuRef = ref(null);
useFocusTrap(mobileMenuRef, mobileMenuOpen, {
    onEscape: () => { mobileMenuOpen.value = false; },
});

const publicNetworkLinks = computed(() => (page.props.relatedDomains || []).filter((domain) => domain?.url && domain?.name));

const partnerLinks = computed(() => (page.props.partners || []).filter((p) => p?.url && p?.name));

const seoAliases = computed(() => {
    const raw = props.domain?.settings?.seo_aliases;
    if (!Array.isArray(raw)) return [];
    return raw.filter((s) => typeof s === 'string' && s.trim().length > 0);
});
</script>

<template>
    <div class="min-h-screen overflow-x-hidden antialiased" :style="themeStyles" :data-design-variant-id="designVariant?.id" :data-design-variant-family="designVariant?.family" :data-design-set="activeDesignSetId" :data-design-set-name="activeDesignSet.name" :data-theme="domainTheme?.name">
        <!-- Skip to content link for keyboard nav -->
        <a href="#main-content" class="app-skip-link">Zum Inhalt springen</a>

        <!-- Variant chrome: when a v0X design variant is active, mount its
             real React nav (extracted from the variant file) instead of
             AppLayout's default header. Falls through to default chrome
             when no variant is active. -->
        <VariantChrome v-if="useVariantChrome" :slug="activeVariantSlug" role="nav" :brand="variantBrand" />

        <!-- Navigation -- always solid on the cream editorial layout -->
        <header v-if="!suppressOwnChrome" class="app-header app-header--scrolled fixed top-0 left-0 right-0 z-50">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex items-center justify-between" style="height: 64px;">
                    <!-- Logo -->
                    <Link href="/?noredirect" class="flex items-center gap-2 min-w-0 shrink">
                        <img v-if="logoSrc && !variantBrand" :src="logoSrc" :alt="brandName || brandParts.base"
                             width="200" height="60" decoding="async"
                             style="height: clamp(32px, 8vw, 48px); width: auto; max-width: min(220px, 55vw); object-fit: contain; flex-shrink: 1; min-width: 0;" />
                        <span v-else-if="variantBrand" class="text-xl tracking-tight" style="font-weight: 700; color: var(--brand-primary, #1A1A17);">
                            {{ variantBrand }}
                        </span>
                        <span v-else-if="brandName" class="text-xl tracking-tight" style="font-weight: 700; color: var(--brand-primary, #1A1A17);">
                            {{ brandName }}
                        </span>
                        <span v-else class="text-xl tracking-tight" style="font-weight: 700; color: var(--brand-primary, #1A1A17);">
                            {{ brandParts.base }}<span style="color: var(--brand-accent-fg, var(--brand-accent));">.{{ brandParts.tld }}</span>
                        </span>
                    </Link>

                    <!-- Desktop Nav -->
                    <nav class="hidden lg:flex items-center gap-1" aria-label="Hauptnavigation">
                        <Link
                            v-for="link in effectiveNavLinks" :key="link.key"
                            :href="link.href"
                            :class="[
                                'app-nav-link text-[13px] font-medium whitespace-nowrap inline-flex items-center cursor-pointer',
                                activePage === link.key
                                    ? 'app-nav-link--scrolled-active'
                                    : 'app-nav-link--scrolled',
                            ]"
                            :aria-current="activePage === link.key ? 'page' : undefined"
                        >{{ link.label }}</Link>
                    </nav>

                    <!-- Right: CTA -->
                    <div class="flex items-center gap-4">
                        <Link
                            v-if="variantCta"
                            :href="variantCta.href"
                            class="app-cta-btn hidden sm:inline-flex items-center px-5 text-sm cursor-pointer"
                            style="background: var(--brand-accent); color: var(--brand-accent-text, #ffffff); border-radius: 999px; font-weight: 600; height: 40px; letter-spacing: 0; text-decoration: none;"
                        >{{ variantCta.label }}</Link>
                        <a v-else href="/#lead-form-section" @click.prevent="scrollToFormOrNavigate" class="app-cta-btn hidden sm:inline-flex items-center px-5 text-sm cursor-pointer" style="background: var(--brand-accent); color: var(--brand-accent-text, #ffffff); border-radius: 999px; font-weight: 600; height: 40px; letter-spacing: 0;">
                            {{ brandCta || 'Angebote erhalten' }}
                        </a>
                        <button
                            @click="mobileMenuOpen = !mobileMenuOpen"
                            :aria-label="mobileMenuOpen ? 'Menu schließen' : 'Menu öffnen'"
                            :aria-expanded="String(mobileMenuOpen)"
                            aria-controls="mobile-menu"
                            class="lg:hidden flex items-center justify-center rounded-lg cursor-pointer text-gray-600"
                            style="min-width: 44px; min-height: 44px;"
                        >
                            <Bars3Icon v-if="!mobileMenuOpen" class="w-6 h-6" aria-hidden="true" />
                            <XMarkIcon v-else class="w-6 h-6" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>

            <!-- Mobile Menu Backdrop -->
            <Transition name="mobile-backdrop">
                <div
                    v-if="mobileMenuOpen"
                    class="lg:hidden fixed inset-0 z-[-1]"
                    style="background: rgba(0, 0, 0, 0.3); backdrop-filter: blur(2px); -webkit-backdrop-filter: blur(2px);"
                    @click="mobileMenuOpen = false"
                    aria-hidden="true"
                ></div>
            </Transition>

            <!-- Mobile Menu -->
            <Transition name="mobile-slide">
                <nav v-if="mobileMenuOpen" id="mobile-menu" ref="mobileMenuRef" class="lg:hidden bg-white border-t shadow-lg" role="navigation" aria-label="Mobile Navigation">
                    <div class="max-w-6xl mx-auto px-4 py-3 space-y-1">
                        <Link
                            v-for="link in effectiveNavLinks" :key="`m-${link.key}`"
                            :href="link.href"
                            @click="mobileMenuOpen = false"
                            class="app-mobile-link block px-4 flex items-center text-sm active:bg-gray-50 transition-colors cursor-pointer"
                            style="min-height: 48px;"
                            :style="activePage === link.key ? 'color: var(--brand-accent-fg, var(--brand-accent)); font-weight: 600;' : 'color: #666;'"
                            :aria-current="activePage === link.key ? 'page' : undefined"
                        >{{ link.label }}</Link>
                        <Link
                            v-if="variantCta"
                            :href="variantCta.href"
                            @click="mobileMenuOpen = false"
                            class="block w-full text-center text-white px-4 flex items-center justify-center text-sm mt-2 active:opacity-90 transition-opacity cursor-pointer"
                            style="background: linear-gradient(135deg, var(--brand-accent) 0%, var(--brand-accent) 100%); color: var(--brand-accent-text, #fff); border-radius: 24px; font-weight: 700; min-height: 48px; letter-spacing: 0.02em; border: 1px solid rgba(255,255,255,0.18); box-shadow: 0 4px 14px rgba(37, 99, 235, 0.25); text-decoration: none;"
                        >{{ variantCta.label }}</Link>
                        <a v-else href="/#lead-form-section" @click.prevent="mobileMenuOpen = false; scrollToFormOrNavigate()" class="block w-full text-center text-white px-4 flex items-center justify-center text-sm mt-2 active:opacity-90 transition-opacity cursor-pointer" style="background: linear-gradient(135deg, var(--brand-accent) 0%, var(--brand-accent) 100%); color: var(--brand-accent-text, #fff); border-radius: 24px; font-weight: 700; min-height: 48px; letter-spacing: 0.02em; border: 1px solid rgba(255,255,255,0.18); box-shadow: 0 4px 14px rgba(37, 99, 235, 0.25);">
                            {{ brandCta || 'Angebote erhalten' }}
                        </a>
                    </div>
                </nav>
            </Transition>
        </header>

        <!-- Content. The default chrome uses a fixed header so we
             reserve `pt-16`; variant chromes flow inline so no offset. -->
        <main id="main-content" :class="suppressOwnChrome ? '' : 'pt-16'" v-glossary>
            <slot />
        </main>

        <!-- Variant footer chrome: real footer extracted from each
             v0X variant. Replaces AppLayout's default footer when a
             variant is active. -->
        <VariantChrome v-if="useVariantChrome" :slug="activeVariantSlug" role="footer" :brand="variantBrand" />

        <!-- Footer -->
        <footer v-if="!suppressOwnChrome" class="app-footer" style="padding-bottom: env(safe-area-inset-bottom, 0px);">
            <!-- Trust bar (site-wide) -->
            <div class="app-trust-bar" aria-label="Vertrauenssignale">
                <div class="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-4">
                    <ul class="app-trust-bar__list" style="list-style: none; padding: 0; margin: 0;">
                        <li v-for="item in trustBarItems" :key="item" class="app-trust-bar__item">
                            <span class="app-trust-bar__icon" aria-hidden="true">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" focusable="false"><polyline points="20 6 9 17 4 12"/></svg>
                            </span>
                            {{ item }}
                        </li>
                    </ul>
                </div>
            </div>
            <div class="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
                <!-- Seiten -->
                <div class="border-b border-black/10 py-5">
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <Link href="/seiten" title="Alle Seiten" class="app-footer-link cursor-pointer" style="font-size: 12px; text-decoration: none;">Seiten</Link>
                        <div class="flex flex-wrap gap-3">
                            <Link
                                v-for="link in pageLinks"
                                :key="link.href"
                                :href="link.href"
                                :title="link.label"
                                class="app-footer-link cursor-pointer"
                                style="font-size: 12px; text-decoration: none; font-weight: 600;"
                            >{{ link.label }}</Link>
                        </div>
                    </div>
                </div>
                <!-- Produkte & Services -->
                <div v-if="!isWaemoBrand" class="border-b border-black/10 py-5">
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <Link href="/produkte" title="Alle Produkte & Services" class="app-footer-link cursor-pointer" style="font-size: 12px; text-decoration: none;">Produkte & Services</Link>
                        <div class="flex flex-wrap gap-3">
                            <Link
                                v-for="link in serviceLinks"
                                :key="link.href"
                                :href="link.href"
                                :title="link.label"
                                class="app-footer-link cursor-pointer"
                                style="font-size: 12px; text-decoration: none; font-weight: 600;"
                            >{{ link.label }}</Link>
                        </div>
                    </div>
                </div>
                <!-- Tools & Wissen -->
                <div v-if="!isWaemoBrand" class="border-b border-black/10 py-5">
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <Link href="/tools-wissen" title="Alle Tools & Wissen" class="app-footer-link cursor-pointer" style="font-size: 12px; text-decoration: none;">Tools & Wissen</Link>
                        <div class="flex flex-wrap gap-3">
                            <Link
                                v-for="tool in toolLinks"
                                :key="tool.href"
                                :href="tool.href"
                                :title="tool.label"
                                class="app-footer-link cursor-pointer"
                                style="font-size: 12px; text-decoration: none; font-weight: 600;"
                            >{{ tool.label }}</Link>
                        </div>
                    </div>
                </div>
                <!-- Weitere Fachportale (dofollow network links -- SEO only, visually hidden) -->
                <div v-if="publicNetworkLinks.length && !isWaemoBrand" class="border-b border-black/10 py-5" style="position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;">
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <Link href="/netzwerk" title="Fachportale für Photovoltaik und Wärmepumpen" class="app-footer-link cursor-pointer" style="font-size: 12px; text-decoration: none;">Fachportale im Netzwerk</Link>
                        <div class="flex flex-wrap gap-3">
                            <a
                                v-for="domainLink in publicNetworkLinks"
                                :key="domainLink.name"
                                :href="domainLink.url"
                                :title="`${domainLink.label} – Photovoltaik und Wärmepumpen Angebote vergleichen`"
                                rel="noopener"
                                class="app-footer-link cursor-pointer"
                                style="font-size: 12px; text-decoration: none; font-weight: 600;"
                            >
                                {{ domainLink.label }}
                            </a>
                        </div>
                    </div>
                </div>
                <!-- Partner (dofollow -- SEO only, visually hidden) -->
                <div v-if="partnerLinks.length && !isWaemoBrand" class="border-b border-black/10 py-5" style="position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;">
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <Link href="/partner" title="Geprüfte Fachbetriebe für Photovoltaik und Wärmepumpen" class="app-footer-link cursor-pointer" style="font-size: 12px; text-decoration: none;">Geprüfte Fachbetriebe</Link>
                        <div class="flex flex-wrap gap-3">
                            <a
                                v-for="partner in partnerLinks"
                                :key="partner.name"
                                :href="partner.url"
                                :title="`${partner.name} – ${partner.title || 'Photovoltaik und Wärmepumpen Fachbetrieb'}`"
                                rel="noopener"
                                class="app-footer-link cursor-pointer"
                                style="font-size: 12px; text-decoration: none; font-weight: 600;"
                            >
                                {{ partner.name }}
                            </a>
                        </div>
                    </div>
                </div>
                <!-- Folgen Sie uns -->
                <div v-if="socialLinks.length" class="border-b border-black/10 py-5">
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <span style="font-size: 12px;">Folgen Sie uns</span>
                        <div class="flex items-center gap-4">
                            <a
                                v-for="social in socialLinks"
                                :key="social.name"
                                :href="social.url"
                                :title="`${social.name} Profil besuchen`"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="app-footer-link cursor-pointer"
                                style="font-size: 12px; text-decoration: none; font-weight: 600;"
                            >
                                {{ social.name }}
                            </a>
                        </div>
                    </div>
                </div>
                <!-- Redaktion (SEO only, visually hidden) -->
                <div class="border-b border-black/10 py-5" style="position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;">
                    <Link href="/über-uns" title="Über uns" class="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 hover:opacity-80 transition-opacity cursor-pointer" style="text-decoration: none;">
                        <div class="flex items-center gap-3">
                            <div class="app-redaktion-avatar w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold" style="background: var(--brand-accent);">R</div>
                            <div>
                                <div style="font-size: 12px; font-weight: 600;">Redaktion {{ brandName || brandParts.full }}</div>
                                <div style="font-size: 11px;">Fachredaktion Erneuerbare Energien</div>
                            </div>
                        </div>
                        <p style="font-size: 11px; line-height: 1.5; max-width: 480px;">Unser Redaktionsteam prüft Inhalte auf Aktualität und Richtigkeit. Preisdaten basieren auf Markterhebungen und werden regelmäßig aktualisiert.</p>
                    </Link>
                </div>
                <!-- Copyright & Legal -->
                <div class="flex flex-col sm:flex-row items-center justify-between gap-3" style="padding: 18px 0;">
                    <div class="flex flex-col sm:flex-row items-center gap-2">
                        <span style="font-size: 12px;">&copy; {{ new Date().getFullYear() }} {{ brandName || brandParts.full }}. Alle Rechte vorbehalten.</span>
                        <span class="hidden sm:inline" style="font-size: 12px;">|</span>
                        <a :href="`mailto:${contactEmail}`" style="font-size: 12px; text-decoration: none;" class="app-footer-link cursor-pointer">{{ contactEmail }}</a>
                    </div>
                    <div class="flex flex-wrap items-baseline gap-x-5 gap-y-1">
                        <Link href="/impressum" style="font-size: 12px; text-decoration: none;" class="app-footer-link cursor-pointer">Impressum</Link>
                        <Link href="/datenschutz" style="font-size: 12px; text-decoration: none;" class="app-footer-link cursor-pointer">Datenschutz</Link>
                        <Link href="/agb" style="font-size: 12px; text-decoration: none;" class="app-footer-link cursor-pointer">AGB</Link>
                        <Link href="/widerruf" style="font-size: 12px; text-decoration: none;" class="app-footer-link cursor-pointer">Widerruf</Link>
                        <a href="#" @click.prevent="openCookiePreferences" style="font-size: 12px; text-decoration: none;" class="app-footer-link cursor-pointer">Cookies</a>
                    </div>
                </div>

                <!-- SEO alias block: alternative brand spellings for discoverability -->
                <div v-if="seoAliases.length" class="py-5" :style="isBrandDirect ? 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;' : 'border-top: 1px solid rgba(255,255,255,0.08);'">
                    <h2 class="app-seo-aliases__heading" style="font-size: 11px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(255,255,255,0.45); margin-bottom: 10px;">Auch bekannt als</h2>
                    <ul class="app-seo-aliases__list" style="list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px 14px;">
                        <li v-for="alias in seoAliases" :key="alias" style="font-size: 11px; color: rgba(255,255,255,0.5); line-height: 1.6;">{{ alias }}</li>
                    </ul>
                </div>
            </div>
        </footer>

        <!-- Floating back-to-top button -->
        <Transition name="btt-fade">
            <button
                v-if="showBackToTop"
                type="button"
                class="app-floating-btt"
                @click="scrollToTop"
                aria-label="Nach oben scrollen"
                title="Nach oben"
            >
                <ArrowUpIcon class="w-5 h-5" aria-hidden="true" />
            </button>
        </Transition>
    </div>
</template>

<style>
/* --- Skip link for keyboard navigation --- */
.app-skip-link {
    position: absolute;
    top: -100%;
    left: 16px;
    z-index: 100;
    padding: 8px 16px;
    background: var(--dv-card, #fff);
    color: var(--dv-text, #111);
    font-size: 14px;
    font-weight: 600;
    border-radius: 4px;
    text-decoration: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.app-skip-link:focus {
    top: 12px;
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
}

/* --- Header: GPU-composited transitions --- */
.app-header {
    will-change: transform;
    transition: box-shadow 200ms ease-out, backdrop-filter 200ms ease-out;
}

.app-header--transparent {
    background: transparent;
}

/* Glassmorphism header on scroll -- premium accent line */
.app-header--scrolled {
    background: color-mix(in srgb, var(--dv-page, #ffffff) 85%, transparent);
    backdrop-filter: blur(18px) saturate(180%);
    -webkit-backdrop-filter: blur(18px) saturate(180%);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
    border-bottom: none;
}

.app-header--scrolled::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
        90deg,
        transparent 0%,
        var(--brand-accent) 50%,
        transparent 100%
    );
    opacity: 0.45;
}

/* --- Desktop nav links: 44px touch targets, underline-reveal hover --- */
.app-nav-link {
    position: relative;
    min-height: 44px;
    padding: 0 14px;
    border-radius: 6px;
    white-space: nowrap;
    letter-spacing: 0.01em;
}

/* Hero mode (transparent header): light text */
.app-nav-link--hero {
    color: rgba(255, 255, 255, 0.8);
}
.app-nav-link--hero:hover {
    color: #fff;
}
.app-nav-link--hero-active {
    color: #fff;
}

/* Scrolled mode (solid header): dark text */
.app-nav-link--scrolled {
    color: #555;
}
.app-nav-link--scrolled:hover {
    color: #222;
}
.app-nav-link--scrolled-active {
    color: var(--brand-primary, var(--brand-accent-fg, var(--brand-accent)));
}

.app-nav-link::after {
    content: '';
    position: absolute;
    bottom: 10px;
    left: 14px;
    right: 14px;
    height: 2px;
    background: var(--brand-accent);
    border-radius: 1px;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 200ms ease-out;
    will-change: transform;
}

.app-nav-link:hover::after {
    transform: scaleX(1);
}

/* Active nav link indicator */
.app-nav-link--active::after {
    transform: scaleX(1);
    opacity: 1;
}

.app-nav-link:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
}

/* --- CTA button: clean, subtle hover --- */
.app-cta-btn {
    transition: opacity 200ms ease-out;
}

.app-cta-btn:hover {
    opacity: 0.92;
}

.app-cta-btn:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
}

/* --- Mobile backdrop transition --- */
.mobile-backdrop-enter-active {
    transition: opacity 250ms ease-out;
}
.mobile-backdrop-leave-active {
    transition: opacity 200ms ease-in;
}
.mobile-backdrop-enter-from,
.mobile-backdrop-leave-to {
    opacity: 0;
}

/* --- Mobile menu: slide + fade transition --- */
.mobile-slide-enter-active {
    transition: opacity 250ms ease-out, transform 250ms ease-out;
}
.mobile-slide-leave-active {
    transition: opacity 180ms ease-in, transform 180ms ease-in;
}
.mobile-slide-enter-from {
    opacity: 0;
    transform: translateY(-16px);
}
.mobile-slide-leave-to {
    opacity: 0;
    transform: translateY(-16px);
}

/* --- Mobile nav link focus --- */
.app-mobile-link:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: -2px;
}

/* --- Global focus-visible for interactive elements --- */
.app-footer a:focus-visible,
.app-footer button:focus-visible,
header button:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
    border-radius: 3px;
}

/* --- Footer link underline-reveal hover --- */
.app-footer-link {
    position: relative;
    display: inline-block;
    transition: color 150ms ease-out;
}

.app-footer-link::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 1px;
    background: currentColor;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 200ms ease-out;
    will-change: transform;
}

.app-footer-link:hover::after {
    transform: scaleX(1);
}

/* --- Redaktion avatar ring --- */
.app-redaktion-avatar {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.15);
}

/* --- Immersive footer --- */
.app-footer {
    position: relative;
    background: var(--dv-dark, #111827);
    color: rgba(255, 255, 255, 0.7);
    border-top: none;
    overflow: hidden;
}

/* Trust bar above the footer columns */
.app-trust-bar {
    background: rgba(255, 255, 255, 0.04);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.app-trust-bar__list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px 28px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
}
.app-trust-bar__item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
}
.app-trust-bar__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
}
@media (max-width: 640px) {
    .app-trust-bar__list { gap: 8px 18px; font-size: 12px; }
}

.app-footer::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
        90deg,
        transparent 0%,
        var(--brand-accent) 50%,
        transparent 100%
    );
    opacity: 0.5;
}

.app-footer .border-b { border-color: rgba(255, 255, 255, 0.08) !important; }
.app-footer .border-black\/10 { border-color: rgba(255, 255, 255, 0.08) !important; }

.app-footer a,
.app-footer span,
.app-footer button,
.app-footer div,
.app-footer p {
    color: rgba(255, 255, 255, 0.65) !important;
    transition: color 150ms ease-out;
}

/* Improve footer mobile readability: bigger tap targets and spacing */
@media (max-width: 639px) {
    .app-footer .flex-wrap {
        gap: 8px 12px !important;
    }
    .app-footer .flex-wrap a,
    .app-footer .flex-wrap button {
        min-height: 36px;
        display: inline-flex;
        align-items: center;
        font-size: 13px !important;
    }
    .app-footer .py-5 {
        padding-top: 14px !important;
        padding-bottom: 14px !important;
    }
}

.app-footer a {
    cursor: pointer;
}

.app-footer a:hover {
    color: rgba(255, 255, 255, 0.9) !important;
}

.app-footer [style*="font-weight: 600"] {
    color: rgba(255, 255, 255, 0.75) !important;
}

.app-footer [style*="color: #333"],
.app-footer [style*="color: #555"] {
    color: rgba(255, 255, 255, 0.7) !important;
}

/* --- Floating back-to-top button --- */
.app-floating-btt {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 40;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 1px solid var(--dv-border, rgba(0, 0, 0, 0.1));
    background: var(--dv-card, #ffffff);
    color: var(--dv-text, #1e293b);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: background 200ms ease-out, color 200ms ease-out, border-color 200ms ease-out, box-shadow 200ms ease-out, transform 200ms ease-out;
}

.app-floating-btt:hover {
    background: var(--brand-accent);
    color: #ffffff;
    border-color: var(--brand-accent);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
}

.app-floating-btt:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
}

.btt-fade-enter-active,
.btt-fade-leave-active {
    transition: opacity 250ms ease, transform 250ms ease;
}

.btt-fade-enter-from,
.btt-fade-leave-to {
    opacity: 0;
    transform: translateY(12px);
}
</style>
